import moment, { type Moment } from 'moment'

moment.locale(window.jsProps.lang)

// Action Types
const NEXT_MONTH = 'NEXT_MONTH'
const PREV_MONTH = 'PREV_MONTH'
const SELECT_DAY = 'SELECT_DAY'
const GET_EVENTS_FOR_MONTH = 'GET_EVENTS_FOR_MONTH'

export type CalendarApiEventData = {
  eventTyp: number
  eventId: string
  nameDe: string
  nameEn: string
  generalNameDe: string
  generalNameEn: string
  from: string
  to: string
  sortOrder: number
  ticketsAvailable: boolean
}

export type CalendarApiDayData = {
  from: string
  to: string
  events: CalendarApiEventData[]
}

export type CalendarApiData = Record<
  string,
  Record<string, CalendarApiDayData[]>
>

export interface CalendarState {
  month: Moment
  fetchedMonths: string[]
  events: Record<string, unknown>
  selected: Moment
}

interface RootState {
  calendar: CalendarState
}

type PlainAction = { type: string; [key: string]: unknown }
type GetState = () => RootState
type ThunkAction = (dispatch: Dispatch, getState: GetState) => unknown
type Dispatch = (action: PlainAction | ThunkAction) => unknown

type SelectDayAction = {
  type: typeof SELECT_DAY
  day: Moment
}

type GetEventsFulfilledAction = {
  type: `${typeof GET_EVENTS_FOR_MONTH}_FULFILLED`
  payload: {
    month: Moment
    data: CalendarApiData
  }
}

type CalendarAction = SelectDayAction | GetEventsFulfilledAction | PlainAction

const isSelectDayAction = (
  action: CalendarAction,
): action is SelectDayAction => {
  return action.type === SELECT_DAY && moment.isMoment(action.day)
}

// Action Creators
export const nextMonth = (): ThunkAction => {
  return (dispatch, getState) => {
    dispatch(
      getEventsForMonth(getState().calendar.month.clone().add(2, 'month')),
    )
    dispatch({ type: NEXT_MONTH })
  }
}

export const prevMonth = (): ThunkAction => {
  return (dispatch, getState) => {
    dispatch(
      getEventsForMonth(getState().calendar.month.clone().subtract(2, 'month')),
    )
    dispatch({ type: PREV_MONTH })
  }
}

export const selectDay = (day: Moment): SelectDayAction => {
  return {
    type: SELECT_DAY,
    day,
  }
}

export const getEventsForMonth = (month: Moment): ThunkAction => {
  return (dispatch) => {
    return dispatch({
      type: GET_EVENTS_FOR_MONTH,
      payload: new Promise((resolve, reject) => {
        fetch(
          `https://api.miniatur-wunderland.de/v1/months/${month.format('YYYY-MM-DD')}`,
          {
            method: 'GET',
            mode: 'cors',
          },
        )
          .then((response) => {
            response
              .json()
              .then((data: CalendarApiData) => {
                resolve({ month: month.clone(), data })
              })
              .catch(() => {
                reject(
                  new Error(
                    'Es ist ein Fehler aufgetreten, bitte versuchen Sie es erneut.',
                  ),
                )
              })
          })
          .catch((error) => {
            console.error(error)
            reject(
              new Error(
                'Es ist ein Fehler aufgetreten, bitte versuchen Sie es erneut.',
              ),
            )
          })
      }),
    })
  }
}

// Reducer
const initialState: CalendarState = {
  month: moment(),
  fetchedMonths: [],
  events: {},
  selected: moment().startOf('day'),
}

export default function calendarReducer(
  state: CalendarState = initialState,
  action: CalendarAction,
): CalendarState {
  switch (action.type) {
    case NEXT_MONTH:
      return Object.assign({}, state, {
        month: state.month.clone().add(1, 'month'),
      })

    case PREV_MONTH:
      return Object.assign({}, state, {
        month: state.month.clone().subtract(1, 'month'),
      })

    case SELECT_DAY:
      if (isSelectDayAction(action) && action.day.isValid()) {
        return Object.assign({}, state, {
          selected: action.day.clone(),
          month: action.day.clone(),
        })
      }
      return state

    case GET_EVENTS_FOR_MONTH + '_PENDING':
      return Object.assign({}, state, {})

    case GET_EVENTS_FOR_MONTH + '_REJECTED':
      return Object.assign({}, state, {})

    case GET_EVENTS_FOR_MONTH + '_FULFILLED': {
      const fulfilledAction = action as GetEventsFulfilledAction
      const monthKey = fulfilledAction.payload.month.format('YYYY-MM')
      const monthEntry = fulfilledAction.payload.data[monthKey]
      const eventsByDay =
        Array.isArray(monthEntry) &&
        monthEntry.length > 0 &&
        typeof monthEntry[0] === 'object' &&
        monthEntry[0] !== null
          ? (monthEntry[0] as Record<string, unknown>)
          : {}

      // Transform keys from day-only to full date format
      const events = Object.keys(eventsByDay).reduce<Record<string, unknown>>(
        (acc, current) => {
          const newKey = `${fulfilledAction.payload.month.format('YYYY-MM')}-${current}`
          acc[newKey] = eventsByDay[current]
          return acc
        },
        {},
      )

      return Object.assign({}, state, {
        fetchedMonths: [...state.fetchedMonths, monthKey],
        events: Object.assign({}, state.events, events),
      })
    }

    default:
      return state
  }
}
